import {
  ContentEditorClickableElement,
  ContentEditorRoot,
  CustomContentSession,
  LiveChatProvider,
  ResourceCenterBlockType,
  ResourceCenterContentListBlock,
  ResourceCenterData,
  ResourceCenterLiveChatBlock,
  ResourceCenterNavigationState,
  ThemeTypesSetting,
  ResourceCenterBlockContentItem,
  ListAnnouncementsResult,
  AnnouncementDetail,
  AnnouncementPopupStyle,
  AnnouncementSeenSource,
  contentEndReason,
  contentStartReason,
} from '@usertour/types';
import { ResourceCenterStore } from '@/types/store';
import { UsertourComponent, CustomStoreDataContext } from '@/core/usertour-component';
import { rootsHaveButtonConditions } from '@/core/usertour-helper';
import { logger } from '@/utils';
import { CommonActionHandler } from '@/core/action-handlers';
import { StorageKeys, WidgetZIndex } from '@usertour/constants';
import { isDisplayOnlyBlockType, isEqual, storage } from '@usertour/helpers';
import { UsertourLiveChatManager } from '@/core/usertour-live-chat-manager';
import { UsertourTheme } from '@/core/usertour-theme';

const log = logger.scope('resource-center');
export class UsertourResourceCenter extends UsertourComponent<ResourceCenterStore> {
  getAnnouncementBadgeCount(): number {
    return this.getStoreData()?.resourceCenterData?.announcementUnreadCount ?? 0;
  }

  protected initializeActionHandlers(): void {
    this.registerActionHandlers([new CommonActionHandler()]);
  }

  async check(): Promise<void> {
    try {
      await this.checkAndUpdateButtonConditions();
      await this.checkAndUpdateThemeSettings();
      await this.checkAndUpdatePopupAnnouncementTheme();
    } catch (error) {
      log.error('Failed to check the resource center state', error);
    }
  }

  /**
   * Settle the popup announcement's OWN theme from its sessionTheme
   * (variations evaluated in the browser), mirroring what
   * checkAndUpdateThemeSettings does for the panel theme. The server seeds
   * popup.themeSettings with the theme's BASE settings only — without this
   * settle the popup ignored dark-mode/etc. variations while every other
   * surface honored them (acceptance-review find). No-op when the payload
   * carries no sessionTheme (older server) or nothing changed.
   */
  private async checkAndUpdatePopupAnnouncementTheme(): Promise<void> {
    const resourceCenterData = this.getStoreData()?.resourceCenterData;
    const popup = resourceCenterData?.popupAnnouncement;
    if (!resourceCenterData || !popup?.sessionTheme) {
      return;
    }
    const settled = await UsertourTheme.getThemeSettings(popup.id, popup.sessionTheme);
    if (!settled || isEqual(popup.themeSettings, settled)) {
      return;
    }
    this.updateStore({
      resourceCenterData: {
        ...resourceCenterData,
        popupAnnouncement: { ...popup, themeSettings: settled },
      },
    });
  }

  async show(): Promise<void> {
    const storeData = await this.buildStoreData();
    if (!storeData) {
      log.warn('Cannot open: the store data could not be built (is the theme missing?)');
      return;
    }
    if (!storeData.resourceCenterData) {
      log.warn('Cannot open: the session has no resource center data');
      return;
    }
    this.setStoreData({ ...storeData, openState: true });
    this.liveChatManager.configure(storeData.resourceCenterData);
  }

  async update(session: CustomContentSession): Promise<void> {
    this.updateSession(session);
    await this.refreshStoreData();
    const data = this.getStoreData()?.resourceCenterData;
    if (data) {
      this.liveChatManager.configure(data);
    }
  }

  async handleDismiss(): Promise<void> {
    await this.close(contentEndReason.DISMISS_BUTTON);
  }

  async handleOnClick(element: ContentEditorClickableElement): Promise<void> {
    const { type, data } = element;
    if (type === 'button' && data.actions) {
      await this.handleActions(data.actions);
    }
  }

  async expand(expanded: boolean): Promise<void> {
    const store = this.getStoreData();
    if (!store) return;
    if (store.expanded === expanded) return;
    this.setExpandedStateStorage(this.getSessionId(), expanded);
    this.updateStore({ expanded });

    // When resource center expands, force-close any open live chat provider (bidirectional management)
    if (expanded && this.liveChatManager.isActive) {
      this.liveChatManager.close();
      this.updateStore({ liveChatActive: false, liveChatProviderOpen: false });
    }

    // Report open/close events
    const sessionId = this.getSessionId();
    if (expanded) {
      this.socketService.openResourceCenter({ sessionId });
    } else {
      this.socketService.closeResourceCenter({ sessionId });
    }
  }

  async handleBlockClick(blockId: string): Promise<void> {
    const store = this.getStoreData();
    // Search for block across all tabs
    let block = null;
    for (const tab of store?.resourceCenterData?.tabs ?? []) {
      block = tab.blocks.find((b) => b.id === blockId);
      if (block) break;
    }
    if (!block || isDisplayOnlyBlockType(block.type)) {
      return;
    }
    const sessionId = this.getSessionId();
    this.socketService.clickResourceCenter({ sessionId, blockId });

    // ACTION is a CTA, not navigation: run any configured actions and
    // collapse back to launcher with the same semantics as the user
    // clicking the close button (clears storage, fires closeResourceCenter).
    // Sub-page / content-list clicks fall through and stay expanded so the
    // user can navigate inside the RC.
    if (block.type === ResourceCenterBlockType.ACTION) {
      if (block.clickedActions?.length > 0) {
        await this.handleActions(block.clickedActions);
      }
      await this.expand(false);
    }
  }

  async handleContentListNavigate(
    block: ResourceCenterContentListBlock,
  ): Promise<ResourceCenterBlockContentItem[]> {
    const sessionId = this.getSessionId();
    // Loading state so a retry (see below) gives feedback for up to the socket's
    // emit timeout instead of looking inert.
    this.updateStore({ contentListLoading: true, contentListError: false });
    try {
      const items = await this.socketService.listResourceCenterBlockContent({
        sessionId,
        blockId: block.id,
      });

      // null = the fetch failed (timeout / dropped socket), distinct from an
      // empty list. Flag the error so the view offers a retry rather than
      // rendering "No items"; keep the previous items untouched.
      if (items === null) {
        this.updateStore({ contentListError: true, contentListLoading: false });
        return [];
      }

      // Enrich server response with per-item config (icon, navigate URL) from block data.
      // Server already filters items by onlyShowItemConditions, so we trust its list.
      // The label overrides the server-supplied admin name; the block comes from
      // the localized session payload, so a translated label arrives translated.
      const configMap = new Map(block.contentItems.map((ci) => [ci.contentId, ci]));
      const enrichedItems: ResourceCenterBlockContentItem[] = items.map((item) => {
        const config = configMap.get(item.contentId);
        if (!config) return item;
        return {
          ...item,
          ...(config.label && { name: config.label }),
          ...(config.iconSource && { iconSource: config.iconSource }),
          ...(config.iconType && { iconType: config.iconType }),
          ...(config.iconUrl && { iconUrl: config.iconUrl }),
          ...(config.navigateUrl && { navigateUrl: config.navigateUrl }),
          ...(config.navigateOpenType && { navigateOpenType: config.navigateOpenType }),
        };
      });

      this.updateStore({
        contentListItems: enrichedItems,
        contentListError: false,
        contentListLoading: false,
      });
      return enrichedItems;
    } catch (error) {
      log.error('Failed to load the content list', error);
      this.updateStore({ contentListError: true, contentListLoading: false });
      return [];
    }
  }

  async handleContentListItemClick(item: ResourceCenterBlockContentItem): Promise<void> {
    try {
      await this.socketService.startContent({
        contentId: item.contentId,
        startReason: contentStartReason.START_FROM_ACTION,
      });

      // Navigate to URL if configured on this item
      if (item.navigateUrl && item.navigateUrl.length > 0) {
        this.handleNavigate({
          value: item.navigateUrl,
          openType: item.navigateOpenType ?? 'same',
        });
      }

      // Clicking a list item launches content — collapse with the same
      // semantics as the user clicking the close button.
      await this.expand(false);
    } catch (error) {
      log.error('Failed to start content from the content list', error);
    }
  }

  // ── Announcement operations ──────────────────────────────────────────

  // Arrow properties (like handleLiveChatClick): stable identities that can be
  // passed to the widget directly, instead of inline arrows re-created every
  // render — which forced the widget to mirror them into refs to avoid
  // re-running its load effects on every provider re-render.
  listAnnouncements = async (): Promise<ListAnnouncementsResult | null> => {
    try {
      // null = load failed (the socket layer already maps timeout/malformed to
      // null); the feed distinguishes it from an empty feed and offers a retry.
      return await this.socketService.listAnnouncements();
    } catch (error) {
      log.error('Failed to load announcements', error);
      return null;
    }
  };

  getAnnouncement = async (contentId: string): Promise<AnnouncementDetail | null> => {
    try {
      return await this.socketService.getAnnouncement({ contentId });
    } catch (error) {
      log.error('Failed to load the announcement', error);
      return null;
    }
  };

  markAnnouncementsSeen = async (
    items: { contentId: string }[],
    source?: AnnouncementSeenSource,
  ): Promise<boolean> => {
    if (items.length === 0) {
      return true;
    }

    // The popup's presence mirrors unread state exactly: it retires when (and
    // only when) its announcement is marked seen — by the feed including it in
    // its batch, or by the popup's own dismiss. Panel open/close never touches
    // it, so collapsing an un-read panel brings the bubble back.
    const popup = this.getStoreData()?.resourceCenterData?.popupAnnouncement;
    if (popup && items.some((item) => item.contentId === popup.id)) {
      this.clearPopupAnnouncementLocally();
    }

    // Optimistically drop the launcher badge so collapsing back to the launcher
    // reflects the just-opened feed without waiting for the next session rebuild.
    // The server's announcementUnreadCount stays the source of truth and reconciles on the
    // next build; this only avoids a stale badge for the rest of this session.
    // The feed sends one batch covering exactly its unseen items, so decrementing
    // by the batch size matches what the server will mark.
    this.decrementAnnouncementUnreadCount(items.length);
    try {
      return await this.socketService.markAnnouncementsSeen({ items, source });
    } catch (error) {
      log.error('Failed to mark announcements as seen', error);
      // Accepted trade-off: the optimistic updates above are NOT rolled back
      // and there is no retry. Sub-30s disconnects never land here (socket.io
      // buffers the emit and flushes on reconnect); on a longer outage the
      // server never records seen, so the popup/badge legitimately return on
      // the next session build — the server stays the source of truth and the
      // state self-heals. Rolling back would flash the popup back mid-session
      // for a marginal gain; a retry queue is not worth the machinery.
      return false;
    }
  };

  /**
   * Dismiss the self-presenting popup announcement by marking it seen — the
   * mark path retires the local payload synchronously and the server stops
   * delivering it on future session builds. Every popup interaction (close,
   * backdrop, read more, content action) funnels through here.
   */
  dismissPopupAnnouncement = async (): Promise<void> => {
    const popup = this.getStoreData()?.resourceCenterData?.popupAnnouncement;
    if (!popup) {
      return;
    }

    const source: AnnouncementSeenSource =
      popup.popupConfig.style === AnnouncementPopupStyle.MODAL ? 'modal' : 'bubble';
    await this.markAnnouncementsSeen([{ contentId: popup.id }], source);
  };

  /**
   * Drop the popup payload from the local store. Called only from
   * markAnnouncementsSeen when its batch covers the popup announcement, so the
   * popup's presence always mirrors the announcement's unread state.
   */
  private clearPopupAnnouncementLocally(): void {
    const resourceCenterData = this.getStoreData()?.resourceCenterData;
    if (!resourceCenterData?.popupAnnouncement) {
      return;
    }
    const { popupAnnouncement: _dismissed, ...remaining } = resourceCenterData;
    this.updateStore({ resourceCenterData: remaining });
  }

  /**
   * Decrement the global announcement unread count (clamped at zero), which
   * drives the launcher badge.
   */
  private decrementAnnouncementUnreadCount(count: number): void {
    const resourceCenterData = this.getStoreData()?.resourceCenterData;
    const current = resourceCenterData?.announcementUnreadCount ?? 0;
    if (!resourceCenterData || current <= 0) {
      return;
    }
    this.updateStore({
      resourceCenterData: {
        ...resourceCenterData,
        announcementUnreadCount: Math.max(0, current - count),
      },
    });
  }

  // ── Live chat provider lifecycle ─────────────────────────────────────

  private liveChatManager = new UsertourLiveChatManager({
    onProviderClose: () => {
      this.updateStore({ liveChatActive: false, liveChatProviderOpen: false });
    },
    isEvalJsDisabled: () => this.isEvalJsDisabled(),
  });

  handleLiveChatClick = async (block: ResourceCenterLiveChatBlock): Promise<void> => {
    try {
      await this.handleBlockClick(block.id);
      // CUSTOM provider is fire-and-forget: run the user's code and collapse
      // to launcher with the same semantics as the user clicking the close
      // button. Unlike managed providers, there's no "session" to hide the
      // RC for — doing so would leave RC invisible forever (no close signal
      // ever arrives to flip liveChatActive back to false).
      if (block.liveChatProvider === LiveChatProvider.CUSTOM) {
        this.liveChatManager.executeCustomCode(block);
        await this.expand(false);
        return;
      }
      // A provider whose script is missing on the page cannot open — and no
      // close event would ever arrive to un-hide the RC (liveChatActive would
      // stay true forever, the launcher invisible until a full reload). Fall
      // back to a plain collapse, same as the CUSTOM fire-and-forget path.
      if (!this.liveChatManager.open(block)) {
        await this.expand(false);
        return;
      }
      // Hide RC and mark live chat active in a single render to avoid the
      // launcher flashing between expanded:false and liveChatActive:true.
      // Mirror expand(false)'s persistence + analytics so closing-via-chat
      // matches closing-via-button.
      const sessionId = this.getSessionId();
      this.setExpandedStateStorage(sessionId, false);
      this.updateStore({ expanded: false, liveChatActive: true, liveChatProviderOpen: true });
      this.socketService.closeResourceCenter({ sessionId });
    } catch (error) {
      log.error('Failed to open live chat', error);
    }
  };

  protected onDestroy(): void {
    this.liveChatManager.dispose();
  }

  isExpandable(): boolean {
    const store = this.getStoreData();
    return store?.expanded === false;
  }

  setLauncherHidden(hidden: boolean): void {
    this.updateStore({ launcherHidden: hidden });
  }

  protected getZIndex(themeSettings?: ThemeTypesSetting): number {
    const themeZIndex = themeSettings?.resourceCenter?.zIndex;
    if (themeZIndex != null) {
      return themeZIndex;
    }
    return this.getBaseZIndex() + WidgetZIndex.RESOURCE_CENTER_OFFSET;
  }

  /**
   * Re-evaluates button disable/hide conditions inside rich-text blocks against
   * the live page — same defect class as the tour (see usertour-tour.ts
   * checkAndUpdateButtonConditions). Rebuilds tabs immutably (the store must
   * not share references with the source data).
   * @private
   */
  private async checkAndUpdateButtonConditions(): Promise<void> {
    const store = this.getStoreData();
    const resourceCenterData = store?.resourceCenterData;
    if (!resourceCenterData || !rootsHaveButtonConditions(resourceCenterData.tabs)) {
      return;
    }
    let changed = false;
    const tabs = await Promise.all(
      resourceCenterData.tabs.map(async (tab) => {
        const blocks = await Promise.all(
          tab.blocks.map(async (block) => {
            if (
              block.type !== ResourceCenterBlockType.RICH_TEXT ||
              !block.content ||
              !rootsHaveButtonConditions(block.content)
            ) {
              return block;
            }
            const evaluated = await this.evaluateButtonConditionsInData(
              block.content as ContentEditorRoot[],
            );
            if (isEqual(evaluated, block.content)) {
              return block;
            }
            changed = true;
            return { ...block, content: evaluated as typeof block.content };
          }),
        );
        return { ...tab, blocks };
      }),
    );
    if (changed) {
      this.updateStore({
        resourceCenterData: { ...resourceCenterData, tabs },
      } as unknown as Partial<ResourceCenterStore>);
    }
  }

  protected async getCustomStoreData(
    _context: CustomStoreDataContext,
  ): Promise<Partial<ResourceCenterStore>> {
    const resourceCenterData = this.getResourceCenterData();
    // Evaluate button conditions in message block contents across all tabs
    if (resourceCenterData) {
      for (const tab of resourceCenterData.tabs) {
        for (const block of tab.blocks) {
          if (block.type === ResourceCenterBlockType.RICH_TEXT && block.content) {
            block.content = (await this.evaluateButtonConditionsInData(
              block.content as ContentEditorRoot[],
            )) as typeof block.content;
          }
        }
      }
    }
    const expanded = this.getExpandedStateStorage(this.getSessionId());
    const initialNav = this.getNavStateStorage(this.getSessionId());
    return {
      resourceCenterData,
      expanded,
      initialNav,
    };
  }

  /**
   * Persist the widget's current navigation state (activeTabId + pageStack)
   * so refreshing the page restores the user where they left off.
   */
  persistNavState = (nav: ResourceCenterNavigationState): void => {
    this.setNavStateStorage(this.getSessionId(), nav);
  };

  private getResourceCenterData(): ResourceCenterData | undefined {
    return this.session.getResourceCenterData();
  }

  private setExpandedStateStorage(sessionId: string, expanded: boolean): void {
    const key = `${StorageKeys.RESOURCE_CENTER_EXPANDED}-${sessionId}`;
    if (expanded) {
      storage.setSessionStorage(key, true);
    } else {
      storage.removeSessionStorage(key);
    }
  }

  private getExpandedStateStorage(sessionId: string): boolean {
    const key = `${StorageKeys.RESOURCE_CENTER_EXPANDED}-${sessionId}`;
    return (storage.getSessionStorage(key) as boolean | undefined) ?? false;
  }

  private setNavStateStorage(sessionId: string, nav: ResourceCenterNavigationState): void {
    const key = `${StorageKeys.RESOURCE_CENTER_NAV}-${sessionId}`;
    storage.setSessionStorage(key, nav);
  }

  private getNavStateStorage(sessionId: string): ResourceCenterNavigationState | null {
    const key = `${StorageKeys.RESOURCE_CENTER_NAV}-${sessionId}`;
    return (storage.getSessionStorage(key) as ResourceCenterNavigationState | undefined) ?? null;
  }
}
